import {useEffect, useMemo, useState} from 'react';
import {
  Breadcrumb,
  Dropdown,
  ProgressIndicator,
  Tag,
  TextInput
} from '@/components/atoms';
import {PageHeader, SaveCancelFooter} from '@/components/molecules';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useNavigate} from 'react-router-dom';
import {useSelector, useStore} from 'react-redux';
import {ACTIVITY_TYPE} from '@/types/Activity';
import {getModal} from '@/utils/CommonModal';
import {
  resetActivityData,
  setActivityFormData
} from '@/store/slices/activitySlice';
import {useDispatch} from 'react-redux';
import {useDropdown} from '@/hooks/Dropdown-hook';
import {DROPDOWN_TYPES} from '@/utils/constants/dropdownConstants';
import {PageContainer} from '@/components/organisms';
import useActivityReview from '@/containers/Activity/ActivityReview/ActivityReview-hook';

/**
 * Placeholder for Survey flow step 2 (Attributes). Setup is on Add Activity; Review follows.
 */
const ActivityAttributes = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const store = useStore();
  const [isCancel, setIsCancel] = useState(false);
  const formData = useSelector((state) => state.activity.formData) || {};
  const {activityType} = formData;
  const {handleDraft: handleReviewDraft} = useActivityReview();
  const [urlTouched, setUrlTouched] = useState(false);
  const [customerTypeTouched, setCustomerTypeTouched] = useState(false);
  const {dropdownList: customerTypeOptions = []} = useDropdown({
    dropdownType: DROPDOWN_TYPES.CustomerType
  });

  const isSurveyFlow = activityType === ACTIVITY_TYPE.SURVEY;
  const customerTypeValues = useMemo(
    () => (Array.isArray(formData.customerType) ? formData.customerType : []),
    [formData.customerType]
  );
  const surveyUrl = formData.url || '';
  const urlError = useMemo(() => {
    if (!surveyUrl || String(surveyUrl).trim() === '') {
      return 'Survey URL is required';
    }
    if (!/^https?:\/\/.+/.test(String(surveyUrl).trim())) {
      return 'Survey URL must start with http:// or https://';
    }
    return '';
  }, [surveyUrl]);
  const customerTypeError = useMemo(() => {
    if (customerTypeValues.length === 0) {
      return 'Customer Type is required';
    }
    return '';
  }, [customerTypeValues]);
  const selectedCustomerTypeOptions = useMemo(
    () =>
      customerTypeOptions.filter((item) =>
        customerTypeValues.includes(String(item?.value))
      ),
    [customerTypeOptions, customerTypeValues]
  );
  const customerTypePlaceholder = useMemo(() => {
    if (selectedCustomerTypeOptions.length === 0) {
      return 'Select customer type';
    }
    if (selectedCustomerTypeOptions.length === 1) {
      return selectedCustomerTypeOptions[0]?.displayText || '1 selected';
    }
    return `${selectedCustomerTypeOptions.length} selected`;
  }, [selectedCustomerTypeOptions]);
  const isFormInvalid = !!urlError || !!customerTypeError;

  useEffect(() => {
    if (activityType && activityType !== ACTIVITY_TYPE.SURVEY) {
      navigate(ROUTES.ACTIVITY_ADD, {replace: true});
    }
  }, [activityType, navigate]);

  const handleCancel = () => setIsCancel(true);

  const onClickPrimaryButton = () => {
    dispatch(resetActivityData({activityType: '', hasEndDate: false}));
    navigate(ROUTES.ACTIVITIES);
  };

  const onClickSecondaryButton = () => setIsCancel(false);

  const getLatestFormData = () => store.getState()?.activity?.formData || {};

  const handleCustomerTypeChange = (selectedOptions) => {
    const selectedValues = Array.isArray(selectedOptions)
      ? selectedOptions.map((item) => String(item?.value)).filter(Boolean)
      : [];
    const latestFormData = getLatestFormData();
    dispatch(
      setActivityFormData({
        ...latestFormData,
        customerType: selectedValues
      })
    );
  };

  const handleUrlChange = (valueOrEvent) => {
    const nextUrl =
      typeof valueOrEvent === 'string'
        ? valueOrEvent
        : (valueOrEvent?.target?.value ?? '');
    setUrlTouched(true);
    const latestFormData = getLatestFormData();
    dispatch(
      setActivityFormData({
        ...latestFormData,
        url: nextUrl
      })
    );
  };

  const handleCustomerTypeRemove = (valueToRemove) => {
    const latestFormData = getLatestFormData();
    const latestCustomerType = Array.isArray(latestFormData.customerType)
      ? latestFormData.customerType
      : [];
    dispatch(
      setActivityFormData({
        ...latestFormData,
        customerType: latestCustomerType.filter(
          (value) => String(value) !== String(valueToRemove)
        )
      })
    );
    setCustomerTypeTouched(true);
  };

  const handleNext = () => {
    setUrlTouched(true);
    setCustomerTypeTouched(true);
    if (isFormInvalid) return;
    navigate(ROUTES.ACTIVITY_REVIEW);
  };

  if (!isSurveyFlow) {
    return null;
  }

  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {label: '', href: ROUTES.HOME},
            {label: 'Loyalty Campaigns', href: ROUTES.ACTIVITIES},
            {label: 'Add Activity', href: ROUTES.ACTIVITIES}
          ]
        }}
        id='activity_attributes_breadcrumb'
      />
      <PageHeader title='Add Activity' />
      <ProgressIndicator
        currentStep={2}
        titles={[{title: 'Setup'}, {title: 'Attributes'}, {title: 'Review'}]}
        className='mb-6'
        onStepClick={(step) => {
          if (step === 1) navigate(ROUTES.ACTIVITY_ADD);
          if (step === 3) navigate(ROUTES.ACTIVITY_REVIEW);
        }}
      />
      <PageContainer data-testid='activity-attributes-form'>
        <h3 className='mb-6 text-base font-bold text-white'>Attributes</h3>
        <div className='mb-6 w-full max-w-[480px]'>
          <Dropdown
            label='Customer Type*'
            required
            childList={customerTypeOptions}
            placeholder={customerTypePlaceholder}
            selection='multiple'
            value={selectedCustomerTypeOptions}
            isError={customerTypeTouched && !!customerTypeError}
            errorMessage={customerTypeError}
            updateValue={handleCustomerTypeChange}
            isJsonWrap={false}
          />
          {selectedCustomerTypeOptions.length > 0 && (
            <div className='mt-3 flex flex-wrap items-center gap-2'>
              {selectedCustomerTypeOptions.map((option) => (
                <Tag
                  key={option.id}
                  text={option.displayText}
                  color='default'
                  isRemovable={true}
                  onRemove={() => handleCustomerTypeRemove(option.value)}
                />
              ))}
            </div>
          )}
        </div>
        <div className='w-full max-w-[300px]'>
          <TextInput
            className='text-sm placeholder:text-sm'
            value={surveyUrl}
            onChange={handleUrlChange}
            onBlur={() => setUrlTouched(true)}
            label='Survey URL*'
            placeholderText='Enter Survey URL'
            error={urlTouched ? urlError : ''}
            isDark={true}
            type='text'
            id='survey-url'
          />
        </div>
      </PageContainer>
      <SaveCancelFooter
        handleCancel={handleCancel}
        nextAction={handleNext}
        draftAction={handleReviewDraft}
        FormError={isFormInvalid}
        nextButtonTitle='Next'
        wrapperClassName='mt-10 pb-4'
      />
      {isCancel &&
        getModal({
          title: 'Are you sure you want to cancel?',
          text: 'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO',
          showPrimaryButton: true,
          primaryButtonText: 'Yes',
          onClickPrimaryButton: onClickPrimaryButton,
          showSecondaryButton: true,
          secondaryButtonText: 'No',
          onClickSecondaryButton: onClickSecondaryButton,
          showModal: isCancel,
          onCloseModal: handleCancel,
          hideCloseButton: false
        })}
    </>
  );
};

export default ActivityAttributes;
